import { Intent as BlueprintIntent } from '@blueprintjs/core';
import { OverflowMenuHorizontal } from '@carbon/icons-react';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  Body,
  Button,
  Loading,
  Menu,
  MenuItem,
  MenuItems,
  StaticAvatar,
  StaticBadge,
} from 'vet-bones/design-system/components';
import { alphaSort } from 'vet-bones/utils';

import { useAppDispatch } from 'src/ui/app/hooks';
import { DataTable, DataTableColumn } from 'src/ui/components/DataTable';
import { withConfirmationDs } from 'src/ui/components/withConfirmationDs';
import {
  PORTAL_ENDPOINT_USER_TABLE_ADD,
  PORTAL_ENDPOINT_USER_TABLE_COLUMN_ROLE,
  PORTAL_ENDPOINT_USER_TABLE_CONFIRMATION_YES,
  PORTAL_ENDPOINT_USER_TABLE_ROW_ACTIONS_MENU,
  PORTAL_ENDPOINT_USER_TABLE_ROW_ACTIONS_MENU_ITEM,
  PORTAL_ENDPOINT_USER_TABLE_ROW_AVATAR,
  PORTAL_ENDPOINT_USER_TABLE_ROW_NAME,
  PORTAL_ENDPOINT_USER_TABLE_ROW_ROLE,
  PORTAL_ENDPOINT_USER_TABLE_ROW_STATUS,
  PORTAL_ENDPOINT_USER_TABLE_SEARCH,
} from 'src/ui/constants/testIds';
import { getUserInfo } from 'src/ui/containers/endpoints/utils/getUserItem';
import { useInvalidateConnection } from 'src/ui/containers/endpoints/utils/invalidate';
import { handleMutationResult } from 'src/ui/containers/organization/hooks/helpers';
import { queueMessage, queueNotification } from 'src/ui/features/notifications';
import { DialogType, openedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  useAddConnectionInvitationsMutation,
  useDeleteInvitationsMutation,
  useListConnectionUsersQuery,
  useRevokePersonalConnectionAccessMutation,
} from 'src/ui/graph/types';
import { useCurrentConnection, useProfile } from 'src/ui/hooks/connection';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import * as copy from 'src/ui/templates/copy';

type UserStatus = 'owner' | 'active' | 'invited' | 'expired';

interface UserTableItem {
  id: string;
  initials: string;
  name?: string;
  email: string;
  roles: string[];
  status: UserStatus;
  invitationId?: string;
}

const COLUMNS: Array<DataTableColumn<UserTableItem>> = [
  {
    key: 'name',
    className: 'organization-table-row-user-name',
    header: copy.components.endpoints.userTable.columns.name,
    sortByAlpha: (item: UserTableItem) => item.name || item.email,
  },
  {
    key: 'role',
    className: 'organization-table-row-user-role',
    header: copy.components.endpoints.userTable.columns.role,
    sortByAlpha: (item: UserTableItem) => item.roles.join(', '),
    testId: PORTAL_ENDPOINT_USER_TABLE_COLUMN_ROLE,
  },
  {
    key: 'status',
    className: 'data-table-row-status',
    header: copy.components.endpoints.userTable.columns.status,
    sortByAlpha: (item: UserTableItem) => item.status,
  },
  {
    key: 'action',
    className: 'data-table-row-actions',
    header: copy.components.endpoints.userTable.columns.action,
  },
];

export const PortalEndpointUserTable: React.VFC = () => {
  const { id, org = '' } = useParams<{ id: string; org?: string }>();
  const dispatch = useAppDispatch();
  const onError = useOnError();
  const onMutate = useOnMutate();

  const { data: profile, isLoading: isProfileLoading } = useProfile();
  const { connection, isLoading: isConnectionLoading } = useCurrentConnection(
    id,
    org
  );
  const invalidateConnection = useInvalidateConnection(
    '',
    connection?.id,
    connection?.index
  );

  const {
    data: connectionUsersData,
    isLoading: isConnectionUsersLoading,
  } = useListConnectionUsersQuery(
    graphQLClient,
    { id: connection?.id || '' },
    { enabled: Boolean(connection?.id) }
  );

  const { mutate: resendInvitation } = useAddConnectionInvitationsMutation(
    graphQLClient,
    {
      onMutate,
      onError,
      onSuccess: (data) => {
        invalidateConnection();

        const result = data.addConnectionInvitations?.results?.[0];
        if (!result?.success) {
          dispatch(
            queueNotification(
              result?.error ||
                copy.components.endpoints.userTable.notifications
                  .resendInvitation.error
            )
          );
          return;
        }

        dispatch(
          queueMessage({
            intent: BlueprintIntent.SUCCESS,
            message:
              copy.components.endpoints.userTable.notifications.resendInvitation
                .success,
          })
        );
      },
    }
  );

  const { mutate: deleteInvitation } = useDeleteInvitationsMutation(
    graphQLClient,
    {
      onMutate,
      onError,
      onSuccess: (data) => {
        invalidateConnection();

        const result = data.deleteInvitations?.results?.[0];
        if (!result?.success) {
          dispatch(
            queueNotification(
              result?.error ||
                copy.components.endpoints.userTable.notifications
                  .removeInvitation.error
            )
          );
          return;
        }

        dispatch(
          queueMessage({
            intent: BlueprintIntent.SUCCESS,
            message:
              copy.components.endpoints.userTable.notifications.removeInvitation
                .success,
          })
        );
      },
    }
  );

  const {
    mutate: revokePersonalConnectionAccess,
  } = useRevokePersonalConnectionAccessMutation(graphQLClient, {
    onMutate,
    onError,
    onSuccess: (data, vars) => {
      invalidateConnection();

      const didSucceed = handleMutationResult(
        dispatch,
        data.revokePersonalConnectionAccess,
        copy.components.endpoints.userTable.notifications.removeUser.error,
        vars.input.emails,
        (res) => res.email
      );
      if (!didSucceed) {
        return;
      }

      dispatch(
        queueMessage({
          intent: BlueprintIntent.SUCCESS,
          message:
            copy.components.endpoints.userTable.notifications.removeUser
              .success,
        })
      );
    },
  });

  const userItems = React.useMemo(() => {
    const items: UserTableItem[] = [];
    const userEmails = new Set<string>();
    connectionUsersData?.listUserConnectionAccess?.forEach((access) => {
      const userInfo = getUserInfo(access?.user);
      if (access && userInfo) {
        const roles = (
          access.stardog_roles || []
        ).filter((role): role is NonNullable<typeof role> => Boolean(role));
        items.push({
          ...userInfo,
          roles,
          status:
            userInfo.email === profile?.profile?.email ? 'owner' : 'active',
        });
        userEmails.add(userInfo.email);
      }
    });
    connectionUsersData?.listUserConnectionInvitations?.forEach(
      (invitation) => {
        if (invitation && !userEmails.has(invitation.email)) {
          items.push({
            id: invitation.id,
            initials: invitation.email[0].toUpperCase(),
            email: invitation.email,
            roles: invitation.connection_roles || [],
            status: invitation.expired ? 'expired' : 'invited',
            invitationId: invitation.id,
          });
        }
      }
    );

    items.sort(alphaSort((item) => item.name || item.email));

    return items;
  }, [connectionUsersData, profile]);

  const searchFilter = (item: UserTableItem, query: string): boolean => {
    const trimmed = query.trim().toLowerCase();
    return (
      item.name?.toLowerCase().includes(trimmed) ||
      item.email.toLowerCase().includes(trimmed) ||
      item.roles.some((role) => role.toLowerCase().includes(trimmed))
    );
  };

  const handleAdd = () => {
    dispatch(openedDialog({ type: DialogType.SHARE_PERSONAL_ENDPOINT }));
  };

  const handleEdit = (user: UserTableItem) => {
    dispatch(
      openedDialog({
        type: DialogType.EDIT_SHARED_PERSONAL_ENDPOINT,
        data: { userId: user.id },
      })
    );
  };

  const handleResendInvitation = (user: UserTableItem) => {
    if (!connection?.id) {
      return;
    }
    resendInvitation({
      input: {
        connection_id: connection.id,
        emails: [user.email],
        connection_roles: user.roles,
      },
    });
  };

  const handleRemoveInvitation = (user: UserTableItem) => {
    if (user.invitationId) {
      withConfirmationDs({
        title:
          copy.components.endpoints.userTable.confirmations.removeInvitation
            .title,
        message: copy.components.endpoints.userTable.confirmations.removeInvitation.message(
          user.email
        ),
        yesPrompt:
          copy.components.endpoints.userTable.confirmations.removeInvitation
            .confirm,
        yesPromptTestId: PORTAL_ENDPOINT_USER_TABLE_CONFIRMATION_YES,
        noPrompt:
          copy.components.endpoints.userTable.confirmations.removeInvitation
            .cancel,
        onConfirmationSuccess: () => {
          if (!user.invitationId) {
            return;
          }

          deleteInvitation({ ids: [user.invitationId] });
        },
      });
    }
  };

  const handleRemove = (user: UserTableItem) => {
    withConfirmationDs({
      title: copy.components.endpoints.userTable.confirmations.removeUser.title,
      message: copy.components.endpoints.userTable.confirmations.removeUser.message(
        user.name || user.email
      ),
      yesPrompt:
        copy.components.endpoints.userTable.confirmations.removeUser.confirm,
      yesPromptTestId: PORTAL_ENDPOINT_USER_TABLE_CONFIRMATION_YES,
      noPrompt:
        copy.components.endpoints.userTable.confirmations.removeUser.cancel,
      onConfirmationSuccess: () => {
        if (!connection?.id) {
          return;
        }

        revokePersonalConnectionAccess({
          input: {
            connectionId: connection.id,
            emails: [user.email],
          },
        });
      },
    });
  };

  const renderRow = (user: UserTableItem, userIdx: number) => (
    <>
      <td className="organization-table-row-user-name">
        <div className="organization-table-row-user-name-cell">
          {user.initials ? (
            <StaticAvatar
              size="md"
              testId={PORTAL_ENDPOINT_USER_TABLE_ROW_AVATAR(user.id, userIdx)}
              text={user.initials}
            />
          ) : null}
          <div className="organization-table-row-user-name-title">
            <Body
              ellipsize
              size="md"
              testId={PORTAL_ENDPOINT_USER_TABLE_ROW_NAME(user.id, userIdx)}
            >
              {user.name || user.email}
            </Body>
            {user.name ? (
              <Body
                className="organization-table-row-user-name-subtitle"
                ellipsize
                size="sm"
              >
                {user.email}
              </Body>
            ) : null}
          </div>
        </div>
      </td>
      <td className="organization-table-row-user-roles">
        {user.roles.map((role, roleIdx) => (
          <StaticBadge
            key={role}
            size="md"
            testId={PORTAL_ENDPOINT_USER_TABLE_ROW_ROLE(role, roleIdx)}
            type="default"
            variant="muted"
          >
            {role}
          </StaticBadge>
        ))}
      </td>
      <td className="data-table-row-status">
        <Body
          className={`data-table-row-status--${user.status}`}
          size="md"
          testId={PORTAL_ENDPOINT_USER_TABLE_ROW_STATUS(user.id, userIdx)}
        >
          {copy.components.endpoints.userTable.status[user.status]}
        </Body>
      </td>
      <td className="data-table-row-actions">
        {user.id && user.status !== 'owner' ? (
          <Menu
            leftIcon={OverflowMenuHorizontal}
            menu={
              <MenuItems className="data-table-menu-items">
                {user.status === 'active' ? (
                  <>
                    <MenuItem
                      leftIcon={null}
                      onClick={() => handleEdit(user)}
                      testId={PORTAL_ENDPOINT_USER_TABLE_ROW_ACTIONS_MENU_ITEM(
                        'edit-roles'
                      )}
                      text={
                        copy.components.endpoints.userTable.actions.editRoles
                      }
                    />
                    <MenuItem
                      leftIcon={null}
                      onClick={() => handleRemove(user)}
                      testId={PORTAL_ENDPOINT_USER_TABLE_ROW_ACTIONS_MENU_ITEM(
                        'remove-user'
                      )}
                      text={
                        copy.components.endpoints.userTable.actions.removeUser
                      }
                    />
                  </>
                ) : (
                  <>
                    <MenuItem
                      leftIcon={null}
                      onClick={() => handleResendInvitation(user)}
                      testId={PORTAL_ENDPOINT_USER_TABLE_ROW_ACTIONS_MENU_ITEM(
                        'resend-invitation'
                      )}
                      text={
                        copy.components.endpoints.userTable.actions
                          .resendInvitation
                      }
                    />
                    <MenuItem
                      leftIcon={null}
                      onClick={() => handleRemoveInvitation(user)}
                      testId={PORTAL_ENDPOINT_USER_TABLE_ROW_ACTIONS_MENU_ITEM(
                        'remove-invitation'
                      )}
                      text={
                        copy.components.endpoints.userTable.actions
                          .removeInvitation
                      }
                    />
                  </>
                )}
              </MenuItems>
            }
            size="sm"
            testId={PORTAL_ENDPOINT_USER_TABLE_ROW_ACTIONS_MENU(
              user.id,
              userIdx
            )}
          />
        ) : null}
      </td>
    </>
  );

  if (isProfileLoading || isConnectionLoading || isConnectionUsersLoading) {
    return (
      <div className="organization-dashboard-loading">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <DataTable
      className="organization-table"
      columns={COLUMNS}
      data={userItems}
      getRowKey={(user) => user.id}
      renderRow={renderRow}
      searchActions={
        <Button
          onClick={handleAdd}
          size="md"
          testId={PORTAL_ENDPOINT_USER_TABLE_ADD}
          variant="tertiary-accent"
        >
          {copy.components.endpoints.userTable.add}
        </Button>
      }
      searchFilter={searchFilter}
      searchInputTestId={PORTAL_ENDPOINT_USER_TABLE_SEARCH}
      searchPlaceholder={copy.components.endpoints.userTable.search}
      title={copy.components.endpoints.userTable.title(userItems.length)}
    />
  );
};
